/**
 * Ленивая загрузка страницы, устойчивая к деплою.
 *
 * После выката имена чанков меняются. Открытая вкладка держит в памяти прежний
 * index.html и просит файл, которого на сервере уже нет — импорт падает с
 * «Importing a module script failed». Правильная реакция — один раз
 * перезагрузить страницу: браузер возьмёт свежий index.html с новыми именами.
 * Флаг в sessionStorage не даёт уйти в цикл перезагрузок, если причина другая.
 */
import { lazy } from 'react'
import type { ComponentType, LazyExoticComponent } from 'react'

const RELOAD_FLAG = 'ut-chunk-reload'

function readFlag(): boolean {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === '1'
  } catch {
    return false
  }
}

function writeFlag(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(RELOAD_FLAG, '1')
    else sessionStorage.removeItem(RELOAD_FLAG)
  } catch {
    // приватный режим: перезагрузка просто не будет защищена от повтора
  }
}

// Параметризуемся типом компонента, как React.lazy: вывод по пропсам
// контравариантен и даёт never.
export function lazyPage<T extends ComponentType<object>>(
  load: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await load()
      writeFlag(false)
      return module
    } catch (error) {
      if (!readFlag()) {
        writeFlag(true)
        location.reload()
        // Пока идёт перезагрузка, промис не разрешаем: показывать ошибку незачем.
        await new Promise<never>(() => {})
      }
      throw error
    }
  })
}
